import React from 'react'
import { Box, Typography } from '@mui/material'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { InspectionPreviewItem } from './inspectionUtils'

const MAX_PREVIEW = 3

interface IssuePreviewSummaryProps {
    items: InspectionPreviewItem[]
    areaCount: number
    /** Wording for the empty state and count label */
    variant?: 'tenant_issues' | 'flagged' | 'damages'
}

export const IssuePreviewSummary: React.FC<IssuePreviewSummaryProps> = ({
    items,
    areaCount,
    variant = 'tenant_issues',
}) => {
    const isFlagged = variant === 'flagged'
    const isDamages = variant === 'damages'
    const countLabel = isDamages ? 'damage' : isFlagged ? 'flagged item' : 'issue'
    const emptyLabel = isDamages
        ? 'No damages recorded across'
        : isFlagged
          ? 'No flagged items across'
          : 'No issues reported across'

    if (items.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.03)',
                }}
            >
                <CheckCircleOutlineIcon
                    sx={{ fontSize: 18, color: 'success.main' }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {emptyLabel} {areaCount} area
                    {areaCount === 1 ? '' : 's'}
                </Typography>
            </Box>
        )
    }

    const preview = items.slice(0, MAX_PREVIEW)
    const remaining = items.length - preview.length

    return (
        <Box
            sx={{
                py: 1,
                px: 1.5,
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ReportProblemOutlinedIcon
                    sx={{ fontSize: 18, color: 'warning.main' }}
                />
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}
                >
                    {items.length} {countLabel}
                    {items.length === 1 ? '' : 's'}
                    {isDamages ? ' found' : isFlagged ? ' found' : ' reported'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {preview.map((item) => (
                    <Typography
                        key={item.id}
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.6)',
                            pl: 3.5,
                            fontSize: '0.8125rem',
                        }}
                    >
                        {item.label} — {item.area_name}
                    </Typography>
                ))}
                {remaining > 0 && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.45)',
                            pl: 3.5,
                            fontSize: '0.8125rem',
                            fontStyle: 'italic',
                        }}
                    >
                        +{remaining} more
                    </Typography>
                )}
            </Box>
        </Box>
    )
}
